/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Keyboard Shortcuts Hook
 * Provides keyboard shortcut registration and management
 *
 * Features:
 * - Single-key and multi-key shortcuts
 * - Sequential shortcuts (g+d, g+p pattern like Vim/GitHub)
 * - Platform-aware modifier keys (Cmd on Mac, Ctrl on Windows)
 * - Context-aware shortcuts
 * - Automatic input element detection
 *
 * @example
 * ```tsx
 * // Single-key shortcuts
 * useKeyboardShortcuts([
 *   { keys: "mod+k", action: () => openPalette(), preventDefault: true },
 *   { keys: "mod+shift+p", action: () => createProject(), preventDefault: true },
 * ]);
 *
 * // Sequential shortcuts
 * useSequentialKeyboardShortcuts([
 *   { keys: ["g", "d"], action: () => router.push("/dashboard") },
 *   { keys: ["g", "p"], action: () => router.push("/projects") },
 * ]);
 * ```
 */

"use client";

import {
  formatShortcutKeys,
  globalShortcuts,
  matchesShortcut,
  shortcutCategories,
} from "@/config/keyboard-shortcuts";
import { useEffect, useRef } from "react";

/** Stable empty lists for `getShortcutsByCategory` — inline `?? []` breaks referential equality each call. */
const EMPTY_SHORTCUT_GROUP: [] = [];

// ============================================================================
// TYPES
// ============================================================================

export interface ShortcutConfig {
  /** Key combination (e.g., "mod+k", "mod+shift+p") */
  keys: string;
  /** Function to execute when shortcut is triggered */
  action: (event: KeyboardEvent) => void;
  /** Whether to prevent default browser behavior */
  preventDefault?: boolean;
  /** Whether the shortcut is enabled */
  enabled?: boolean;
}

export interface SequentialShortcutConfig {
  /** Sequence of keys to press (e.g., ["g", "d"]) */
  keys: string[];
  /** Function to execute when sequence is completed */
  action: () => void;
  /** Timeout in ms between keys (default: 1000) */
  timeout?: number;
  /** Whether the shortcut is enabled */
  enabled?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an element should ignore keyboard shortcuts
 */
function isInputElement(element: EventTarget | null): boolean {
  if (!element) return false;
  const target = element as HTMLElement;

  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.contentEditable === "true" ||
    target.getAttribute("role") === "textbox"
  );
}

// ============================================================================
// SINGLE-KEY SHORTCUTS HOOK
// ============================================================================

/**
 * Register keyboard shortcuts that respond to single key combinations
 *
 * @param shortcuts - Array of shortcut configurations
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts([
 *   { keys: "mod+k", action: () => openPalette(), preventDefault: true },
 *   { keys: "mod+s", action: () => save(), preventDefault: true },
 * ]);
 * ```
 */
export function useKeyboardShortcuts(shortcuts: ShortcutConfig[]): void {
  const shortcutsRef = useRef(shortcuts);

  // Keep shortcuts ref updated
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs (unless explicitly allowed)
      if (isInputElement(event.target)) {
        // Allow Cmd+S and Cmd+K even in inputs
        const allowedInInputs = ["mod+s", "mod+k", "mod+p", "escape"];
        const eventMatches = allowedInInputs.some((keys) =>
          matchesShortcut(event, keys),
        );
        if (!eventMatches) return;
      }

      // Check each registered shortcut
      for (const shortcut of shortcutsRef.current) {
        if (shortcut.enabled === false) continue;

        if (matchesShortcut(event, shortcut.keys)) {
          if (shortcut.preventDefault) {
            event.preventDefault();
          }
          shortcut.action(event);
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
}

// ============================================================================
// SEQUENTIAL SHORTCUTS HOOK
// ============================================================================

/**
 * Register keyboard shortcuts that respond to sequential key presses
 * Like Vim or GitHub's "g+d" pattern
 *
 * @param shortcuts - Array of sequential shortcut configurations
 *
 * @example
 * ```tsx
 * useSequentialKeyboardShortcuts([
 *   { keys: ["g", "d"], action: () => router.push("/dashboard") },
 *   { keys: ["g", "p"], action: () => router.push("/projects") },
 * ]);
 * ```
 */
export function useSequentialKeyboardShortcuts(
  shortcuts: SequentialShortcutConfig[],
): void {
  const shortcutsRef = useRef(shortcuts);
  const sequenceRef = useRef<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep shortcuts ref updated
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (isInputElement(event.target)) return;

      // Don't trigger if any modifier keys are pressed
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const key = typeof event.key === "string" ? event.key.toLowerCase() : "";
      if (!key) {
        sequenceRef.current = [];
        return;
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Add key to sequence
      sequenceRef.current.push(key);

      // Check if sequence matches any shortcuts
      for (const shortcut of shortcutsRef.current) {
        if (shortcut.enabled === false) continue;

        const expectedKeys = shortcut.keys.map((k) => k.toLowerCase());
        const currentSequence = sequenceRef.current;

        // Check if current sequence matches the expected keys
        if (currentSequence.length === expectedKeys.length) {
          const matches = expectedKeys.every(
            (expectedKey, index) => currentSequence[index] === expectedKey,
          );

          if (matches) {
            event.preventDefault();
            shortcut.action();
            sequenceRef.current = [];
            return;
          }
        }

        // Check if current sequence is a prefix of expected keys
        const isPrefix = currentSequence.every(
          (key, index) => expectedKeys[index] === key,
        );

        if (!isPrefix) {
          // Sequence doesn't match any shortcuts, reset
          sequenceRef.current = [];
          return;
        }
      }

      // Set timeout to reset sequence
      const timeout = shortcuts.find((s) => s.timeout)?.timeout ?? 1000;
      timeoutRef.current = setTimeout(() => {
        sequenceRef.current = [];
      }, timeout);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [shortcuts]);
}

// ============================================================================
// EXPORTS
// ============================================================================

// Re-export shortcut utilities from config
export { formatShortcutKeys, globalShortcuts, shortcutCategories };

const SHORTCUTS_BY_CATEGORY = (() => {
  const grouped = globalShortcuts.reduce(
    (acc, shortcut) => {
      if (!acc[shortcut.category]) {
        acc[shortcut.category] = [];
      }
      acc[shortcut.category]!.push(shortcut);
      return acc;
    },
    {} as Record<string, Array<(typeof globalShortcuts)[number]>>,
  );

  return {
    navigation: grouped.navigation ?? EMPTY_SHORTCUT_GROUP,
    actions: grouped.actions ?? EMPTY_SHORTCUT_GROUP,
    search: grouped.search ?? EMPTY_SHORTCUT_GROUP,
    global: grouped.global ?? EMPTY_SHORTCUT_GROUP,
  };
})();

/**
 * Get shortcuts grouped by category for display.
 * Result is computed once at module load (`globalShortcuts` is static).
 */
export function getShortcutsByCategory() {
  return SHORTCUTS_BY_CATEGORY;
}
